import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { 
  Plus, 
  Shield, 
  Play, 
  Square, 
  Settings, 
  Trash2,
  Wifi,
  WifiOff,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { vpnService } from '../services/vpnService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const VPN = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const queryClient = useQueryClient();

  const { data: connectionsData, isLoading } = useQuery(
    'vpn-connections',
    vpnService.getConnections
  );

  const { data: statusData } = useQuery(
    'vpn-status',
    vpnService.getStatus,
    { refetchInterval: 10000 } // Odświeżaj co 10 sekund
  );

  const addConnectionMutation = useMutation(vpnService.addConnection, {
    onSuccess: () => {
      queryClient.invalidateQueries('vpn-connections');
      setShowAddModal(false);
      toast.success('Połączenie VPN zostało dodane');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas dodawania połączenia VPN');
    }
  });

  const updateConnectionMutation = useMutation(vpnService.updateConnection, {
    onSuccess: () => {
      queryClient.invalidateQueries('vpn-connections');
      setShowEditModal(false);
      setSelectedConnection(null);
      toast.success('Połączenie VPN zostało zaktualizowane');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas aktualizacji połączenia VPN');
    }
  });

  const deleteConnectionMutation = useMutation(vpnService.deleteConnection, {
    onSuccess: () => {
      queryClient.invalidateQueries('vpn-connections');
      toast.success('Połączenie VPN zostało usunięte');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas usuwania połączenia VPN');
    }
  });

  const connectMutation = useMutation(vpnService.connect, {
    onSuccess: () => {
      queryClient.invalidateQueries('vpn-connections');
      queryClient.invalidateQueries('vpn-status');
      toast.success('Rozpoczęto łączenie z VPN');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas łączenia z VPN');
    }
  });

  const disconnectMutation = useMutation(vpnService.disconnect, {
    onSuccess: () => {
      queryClient.invalidateQueries('vpn-connections');
      queryClient.invalidateQueries('vpn-status');
      toast.success('Połączenie VPN zostało rozłączone');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas rozłączania VPN');
    }
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const onSubmit = (data) => {
    addConnectionMutation.mutate(data);
    reset();
  };

  const handleDelete = (id) => {
    if (window.confirm('Czy na pewno chcesz usunąć to połączenie VPN?')) {
      deleteConnectionMutation.mutate(id);
    }
  };

  const handleConnect = (id) => {
    connectMutation.mutate(id);
  };

  const handleDisconnect = (id) => {
    disconnectMutation.mutate(id);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      connected: {
        class: 'status-connected',
        icon: Wifi,
        text: 'Połączony'
      },
      disconnected: {
        class: 'status-disconnected',
        icon: WifiOff,
        text: 'Rozłączony'
      },
      connecting: {
        class: 'status-connecting',
        icon: Clock,
        text: 'Łączenie...'
      },
      error: {
        class: 'status-error',
        icon: AlertTriangle,
        text: 'Błąd'
      }
    };

    const config = statusConfig[status] || statusConfig.disconnected;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    );
  };

  const getProtocolIcon = (protocol) => {
    switch (protocol) {
      case 'l2tp':
        return <Shield className="h-5 w-5 text-blue-600" />;
      case 'openvpn':
        return <Shield className="h-5 w-5 text-green-600" />;
      case 'pptp':
        return <Shield className="h-5 w-5 text-purple-600" />;
      case 'ikev2':
        return <Shield className="h-5 w-5 text-orange-600" />;
      default:
        return <Shield className="h-5 w-5 text-gray-600" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Dodaj połączenie
        </button>
      </div>

      {/* Status Overview */}
      {statusData && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="p-3 rounded-md bg-blue-100">
                    <Shield className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Wszystkie połączenia
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statusData.status.total}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="p-3 rounded-md bg-green-100">
                    <Wifi className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Połączone
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statusData.status.connected}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="p-3 rounded-md bg-gray-100">
                    <WifiOff className="h-6 w-6 text-gray-600" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Rozłączone
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statusData.status.disconnected}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="p-3 rounded-md bg-red-100">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Błędy
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statusData.status.error}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Connections Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {connectionsData?.connections?.map((connection) => (
          <div key={connection._id} className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {getProtocolIcon(connection.protocol)}
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      {connection.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {connection.serverIP}
                    </p>
                  </div>
                </div>
                {getStatusBadge(connection.status)}
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>Protokół: {connection.protocol.toUpperCase()}</span>
                  <span>
                    {connection.lastConnected && (
                      <>Ostatnio: {new Date(connection.lastConnected).toLocaleDateString('pl-PL')}</>
                    )}
                  </span>
                </div>
              </div>

              {connection.connectionDuration > 0 && (
                <div className="mt-2">
                  <div className="text-sm text-gray-500">
                    Czas połączenia: {Math.floor(connection.connectionDuration / 3600)}h {Math.floor((connection.connectionDuration % 3600) / 60)}m
                  </div>
                </div>
              )}

              <div className="mt-4 flex space-x-2">
                {connection.status === 'connected' ? (
                  <button
                    onClick={() => handleDisconnect(connection._id)}
                    disabled={disconnectMutation.isLoading}
                    className="flex-1 btn-danger text-xs"
                  >
                    {disconnectMutation.isLoading ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <Square className="h-4 w-4 mr-1" />
                        Rozłącz
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => handleConnect(connection._id)}
                    disabled={connectMutation.isLoading || connection.status === 'connecting'}
                    className="flex-1 btn-success text-xs"
                  >
                    {connectMutation.isLoading ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-1" />
                        Połącz
                      </>
                    )}
                  </button>
                )}
                
                <button
                  onClick={() => {
                    setSelectedConnection(connection);
                    setShowEditModal(true);
                  }}
                  className="flex-1 btn-secondary text-xs"
                >
                  <Settings className="h-4 w-4 mr-1" />
                  Ustawienia
                </button>
                
                <button
                  onClick={() => handleDelete(connection._id)}
                  disabled={deleteConnectionMutation.isLoading}
                  className="btn-danger text-xs px-3"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Connection Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Dodaj nowe połączenie VPN
              </h3>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nazwa połączenia
                  </label>
                  <input
                    {...register('name', { required: 'Nazwa jest wymagana' })}
                    type="text"
                    className="input"
                    placeholder="np. Sieć firmowa"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Adres serwera
                  </label>
                  <input
                    {...register('serverIP', { 
                      required: 'Adres serwera jest wymagany',
                      pattern: {
                        value: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?)*$/,
                        message: 'Nieprawidłowy adres IP lub nazwa domeny'
                      }
                    })}
                    type="text"
                    className="input"
                    placeholder="192.168.1.1 lub vpn.example.com"
                  />
                  {errors.serverIP && (
                    <p className="mt-1 text-sm text-red-600">{errors.serverIP.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Protokół
                  </label>
                  <select
                    {...register('protocol', { required: 'Protokół jest wymagany' })}
                    className="input"
                    defaultValue="wireguard"
                  >
                    <option value="wireguard">WireGuard</option>
                    <option value="l2tp">L2TP</option>
                    <option value="openvpn">OpenVPN</option>
                    <option value="pptp">PPTP</option>
                    <option value="ikev2">IKEv2</option>
                  </select>
                  {errors.protocol && (
                    <p className="mt-1 text-sm text-red-600">{errors.protocol.message}</p>
                  )}
                </div>

                {/* Pola dla WireGuard */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Klucz publiczny serwera
                  </label>
                  <textarea
                    {...register('credentials.publicKey')}
                    className="input"
                    rows="3"
                    placeholder="Klucz publiczny serwera WireGuard"
                  />
                  {errors.credentials?.publicKey && (
                    <p className="mt-1 text-sm text-red-600">{errors.credentials.publicKey.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Klucz prywatny klienta
                  </label>
                  <textarea
                    {...register('credentials.privateKey')}
                    className="input"
                    rows="3"
                    placeholder="Klucz prywatny klienta WireGuard"
                  />
                  {errors.credentials?.privateKey && (
                    <p className="mt-1 text-sm text-red-600">{errors.credentials.privateKey.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Pre-shared Key (opcjonalne)
                  </label>
                  <input
                    {...register('credentials.presharedKey')}
                    type="password"
                    className="input"
                    placeholder="••••••••"
                  />
                </div>

                {/* Pola dla L2TP (ukryte domyślnie) */}
                <div style={{ display: 'none' }}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Nazwa użytkownika
                    </label>
                    <input
                      {...register('credentials.username')}
                      type="text"
                      className="input"
                      placeholder="vpn_user"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Hasło
                    </label>
                    <input
                      {...register('credentials.password')}
                      type="password"
                      className="input"
                      placeholder="••••••••"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Pre-shared Key (opcjonalne)
                    </label>
                    <input
                      {...register('credentials.preSharedKey')}
                      type="password"
                      className="input"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 btn-secondary"
                  >
                    Anuluj
                  </button>
                  <button
                    type="submit"
                    disabled={addConnectionMutation.isLoading}
                    className="flex-1 btn-primary"
                  >
                    {addConnectionMutation.isLoading ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      'Dodaj połączenie'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal edycji połączenia VPN */}
      {showEditModal && selectedConnection && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edytuj połączenie VPN
              </h3>
              <form onSubmit={handleSubmit((data) => {
                updateConnectionMutation.mutate({
                  id: selectedConnection._id,
                  ...data
                });
              })}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Nazwa połączenia
                    </label>
                    <input
                      {...register('name', { required: 'Nazwa jest wymagana' })}
                      defaultValue={selectedConnection.name}
                      className="input"
                      placeholder="Nazwa połączenia"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Adres serwera
                    </label>
                    <input
                      {...register('serverIP', { required: 'Adres serwera jest wymagany' })}
                      defaultValue={selectedConnection.serverIP}
                      className="input"
                      placeholder="vpn.example.com"
                    />
                    {errors.serverIP && (
                      <p className="mt-1 text-sm text-red-600">{errors.serverIP.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Protokół
                    </label>
                    <select
                      {...register('protocol', { required: 'Protokół jest wymagany' })}
                      defaultValue={selectedConnection.protocol}
                      className="input"
                    >
                      <option value="wireguard">WireGuard</option>
                      <option value="l2tp">L2TP</option>
                      <option value="openvpn">OpenVPN</option>
                      <option value="pptp">PPTP</option>
                      <option value="ikev2">IKEv2</option>
                    </select>
                    {errors.protocol && (
                      <p className="mt-1 text-sm text-red-600">{errors.protocol.message}</p>
                    )}
                  </div>

                  {/* Pola dla WireGuard */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Klucz publiczny serwera
                    </label>
                    <textarea
                      {...register('credentials.publicKey', { 
                        required: selectedConnection.protocol === 'wireguard' ? 'Klucz publiczny serwera jest wymagany' : false 
                      })}
                      defaultValue={selectedConnection.credentials?.publicKey || ''}
                      className="input"
                      rows="3"
                      placeholder="Klucz publiczny serwera WireGuard"
                    />
                    {errors.credentials?.publicKey && (
                      <p className="mt-1 text-sm text-red-600">{errors.credentials.publicKey.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Klucz prywatny klienta
                    </label>
                    <textarea
                      {...register('credentials.privateKey', { 
                        required: selectedConnection.protocol === 'wireguard' ? 'Klucz prywatny klienta jest wymagany' : false 
                      })}
                      defaultValue={selectedConnection.credentials?.privateKey || ''}
                      className="input"
                      rows="3"
                      placeholder="Klucz prywatny klienta WireGuard"
                    />
                    {errors.credentials?.privateKey && (
                      <p className="mt-1 text-sm text-red-600">{errors.credentials.privateKey.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Pre-shared Key (opcjonalne)
                    </label>
                    <input
                      {...register('credentials.presharedKey')}
                      type="password"
                      defaultValue={selectedConnection.credentials?.presharedKey || ''}
                      className="input"
                      placeholder="••••••••"
                    />
                  </div>

                  {/* Pola dla L2TP (ukryte domyślnie) */}
                  <div style={{ display: 'none' }}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nazwa użytkownika
                      </label>
                      <input
                        {...register('credentials.username')}
                        defaultValue={selectedConnection.credentials?.username || ''}
                        className="input"
                        placeholder="username"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Hasło
                      </label>
                      <input
                        {...register('credentials.password')}
                        type="password"
                        className="input"
                        placeholder="••••••••"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Pre-shared Key (opcjonalne)
                      </label>
                      <input
                        {...register('credentials.preSharedKey')}
                        type="password"
                        defaultValue={selectedConnection.credentials?.preSharedKey || ''}
                        className="input"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditModal(false);
                        setSelectedConnection(null);
                      }}
                      className="flex-1 btn-secondary"
                    >
                      Anuluj
                    </button>
                    <button
                      type="submit"
                      disabled={updateConnectionMutation.isLoading}
                      className="flex-1 btn-primary"
                    >
                      {updateConnectionMutation.isLoading ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        'Zapisz zmiany'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VPN;
